import { useEffect, useState } from "react";
import BayHandoverAPI from "../../api/bayHandover";
import { Button, IconButton } from '../../tailwindcomponents';
import { Typography, LinearProgress, Input, Select, Table, TableCell, TableRow } from './../../tailwindcomponents';
import { BayHandoverIssue } from '../../types/bayHandover';
import { AddCircle, Edit } from '../../components/icons';
import EditColumn from './../Security/EditColumn';
import taskApi from './../../api/tasks';
import { EmployeeSearch } from "../../components/EmployeeSearch";
import { DateTime } from 'luxon';
import { useSelector } from "react-redux";
import { RootState } from "../../reducer";
import StmApi from "../../api/staffmanagement";
import { StaffManagementBays } from "../../types/staffManagement";
import { palette } from '../../theme/palette';

const locationsOptions = ["BAY 1", "BAY 2", "BAY 3", "BAY 4", "BAY 5", "BAY 6", "BAY 7", "HANGAR AIRSITE", "OTHER"];

const BayHandover = () => {
    const store = useSelector((state: RootState) => state);
    const isBayHandoverAdmin = store.user.me.user_clearence_groups.includes("Bay Handover Edit");
    const [issues, setIssues] = useState<BayHandoverIssue[]>([]);
    const [filteredIssues, setFilteredIssues] = useState<BayHandoverIssue[]>([]);
    const [editIndex, setEditIndex] = useState(-1);
    const [allTails, setAllTails] = useState<string[]>([]);
    const [selectedTailNumber, setSelectedTailNumber] = useState<string>("");
    const [selectedIssue, setSelectedIssue] = useState<string>("");
    const [selectedLocation, setSelectedLocation] = useState<string>("");
    const [createIssue, setCreateIssue] = useState<boolean>(false);
    const [selectedCrew, setSelectedCrew] = useState<string>("");

    const [tailSearch, setTailSearch] = useState<string>("");
    const [employeeSearch, setemployeeSearch] = useState<number>(-1);
    const [locationSearch, setLocationSearch] = useState<string>("");
    const [crewSearch, setCrewSearch] = useState<string>("");
    const [bays, setBays] = useState<StaffManagementBays[]>([]);
    const [crewOptions, setCrewOptions] = useState<string[]>([]);
    const [filter, setFilter] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        onSearch();
    }, [tailSearch, employeeSearch, locationSearch, crewSearch]);

    useEffect(() => {
        refresh();
    }, []);

    const refresh = () => {
        setLoading(true);
        setFilter(false);
        setCreateIssue(false);
        setSelectedTailNumber("");
        setSelectedIssue("");
        setEditIndex(-1);
        taskApi.GetAllTailNumbers().then(res => {
            setAllTails(res.data);
        });
        BayHandoverAPI.GetAllBayHandOverIssues().then(res => {
            setIssues(res.data);
        });
        StmApi.GetCrews().then(res => {
            setBays(res.data);
            setLoading(false);
        });
    }
    const onSelectRow = (row: BayHandoverIssue, index: number) => {
        setEditIndex(index);
        setSelectedIssue(row.issue);
        setSelectedTailNumber(row.tail_number);
    }

    const generateTableBody = (row: BayHandoverIssue, index: number) => {
        const user = store.employees.all_employees.find(s => row.created_by === s.user_id);
        return (
            editIndex === index ?
                <TableRow >
                    <TableCell>{DateTime.fromISO(`${row.created_date}`).toFormat('d LLL yyyy')}</TableCell>
                    <TableCell>{user?.name}</TableCell>
                    <TableCell>
                        <Select
                            width={100}
                            widthPercentage
                            filter_by_text="Tail number"
                            onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTailNumber(e.target.value)}
                            options={allTails}
                            selectedItem={selectedTailNumber}
                        />
                    </TableCell>
                    <TableCell>
                        <Input
                            multiline={true}
                            help_text={""}
                            label_text={"Issue"}
                            value={selectedIssue}
                            onTextChange={(text: string) => setSelectedIssue(text)}
                            type="text"
                            width={100}
                        />
                    </TableCell>
                    <TableCell>
                        <Select
                            width={100}
                            widthPercentage
                            filter_by_text="Location"
                            onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => {setSelectedLocation(e.target.value); setSelectedCrew(""); } }
                            options={locationsOptions}
                            selectedItem={selectedLocation}
                        />
                    </TableCell>
                    <TableCell>
                        <Select
                            width={100}
                            widthPercentage
                            filter_by_text="Crew"
                            onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedCrew(e.target.value)}
                            options={bays.find(bay => bay.bay.includes(selectedLocation))?.areas.map(area => area.area) ?? []}
                            selectedItem={selectedCrew}
                        />
                    </TableCell>
                    <EditColumn
                        abort={true}
                        index={index}
                        saveChanges={() => onModifieIssue(row.id)}
                        editRow={index}
                        setEditRow={() => { setEditIndex(-1) }}
                    />
                </TableRow>
                :
                <TableRow >
                    <TableCell>{(row.modified_date_text !== undefined && row.modified_date_text !== null && row.modified_date_text !== "") ? row.modified_date_text : row.created_date_text}</TableCell>
                    <TableCell>{user?.name}</TableCell>
                    <TableCell>{row.tail_number}</TableCell>
                    <TableCell>{row.issue}</TableCell>
                    <TableCell>{row.location}</TableCell>
                    <TableCell>{row.crew}</TableCell>
                    <TableCell>{isBayHandoverAdmin && <IconButton onClick={() => onSelectRow(row, index)}><Edit /></IconButton>}</TableCell>
                </TableRow>
        )
    }

    const onAddIssue = () => {
        BayHandoverAPI.AddIssue(selectedLocation, selectedCrew, selectedIssue, selectedTailNumber).then(res => {
            refresh();
        })
    }

    const onModifieIssue = (issueId: number) => {
        BayHandoverAPI.UpdateIssue(issueId, selectedIssue, selectedTailNumber, selectedLocation, selectedCrew).then(res => {
            refresh();
        })
    }

    const filterIssuesByProperties = (employee: number, location: string, tail: string, crew: string) => {
        return issues.filter(issue => {
            // Check if the properties match the given values (if provided)
            return (
                (employee === -1 || issue.created_by === employee) &&
                (location === '' || issue.location === location) &&
                (tail === '' || issue.tail_number === tail) &&
                (crew === '' || issue.crew === crew)
            );
        });
    }

    const onSearch = () => {
        const tempIssues = filterIssuesByProperties(employeeSearch, locationSearch, tailSearch, crewSearch);
        setFilteredIssues(tempIssues);
        setFilter(true);
    }

    const onClearFilters = () => {
        setFilteredIssues([]);
        setTailSearch("");
        setemployeeSearch(-1);
        setLocationSearch("");
        setCrewSearch("");
        setCrewOptions([]);
        setFilter(false);
    }

    const setCrewAndLocationSearch = (location: string ) => {
        setLocationSearch(location);
        setCrewSearch("");
        const crews = bays.find(bay => bay.bay.includes(location))?.areas.map(area => area.area) ?? [];
        setCrewOptions(crews);
    }

    return (
        <div>
            {loading && <LinearProgress/>}
            <Typography text={'Bay Handover'} variant='h1' align="center" />
            <div>
                <div className="ds-filter-bar">
                    <Select
                        width={100}
                        widthPercentage
                        filter_by_text="Location"
                        onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => setCrewAndLocationSearch(e.target.value)}
                        options={locationsOptions}
                        selectedItem={locationSearch}
                    />
                    <Select
                        width={100}
                        widthPercentage
                        filter_by_text="Crew"
                        onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => setCrewSearch(e.target.value)}
                        options={crewOptions}
                        selectedItem={crewSearch}
                    />
                    <Select
                        width={100}
                        widthPercentage
                        filter_by_text="Tail number"
                        onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => setTailSearch(e.target.value)}
                        options={allTails}
                        selectedItem={tailSearch}
                    />
                    <EmployeeSearch
                        customCloseFunction={() => setemployeeSearch(-1)}
                        place_holder={"Write employee name"}
                        onSelectedEmployee={(employeeId: number) => setemployeeSearch(employeeId || -1)}
                    />
                    <div className="ds-filter-actions">
                        <Button variant="contained" color="primary" onClick={() => onClearFilters()}>Clear filters</Button>
                    </div>
                </div>
                {isBayHandoverAdmin && <div className="flex flex-col items-center mt-7">
                    <IconButton onClick={() => { setCreateIssue(true); setSelectedTailNumber(""); setSelectedLocation("") }} style={{ color: palette.success }} >
                        <AddCircle />
                    </IconButton>
                </div>}
                {createIssue &&
                    <div className="ds-filter-bar">
                        <Input multiline={false} help_text={""} label_text={"Issue"} value={selectedIssue} onTextChange={(text: string) => setSelectedIssue(text)} type="text" width={100} />
                        <Select width={100} widthPercentage filter_by_text="Tail number" onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTailNumber(e.target.value)} options={allTails} selectedItem={selectedTailNumber} />
                        <Select width={100} widthPercentage filter_by_text="Location" onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => {setSelectedLocation(e.target.value); setSelectedCrew(""); }} options={locationsOptions} selectedItem={selectedLocation} />
                        <Select width={100} widthPercentage filter_by_text="Crew" onSelectedItem={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedCrew(e.target.value)} options={bays.find(bay => bay.bay.includes(selectedLocation))?.areas.map(area => area.area) ?? []} selectedItem={selectedCrew} />
                        <div className="ds-filter-actions">
                            <Button variant="contained" color="primary" onClick={() => onAddIssue()}>Add issue</Button>
                            <Button variant="contained" color="secondary" onClick={() => setCreateIssue(false)}>Abort</Button>
                        </div>
                    </div>
                }
                <Table
                    headers={["Date", "Owner", "Tail", "Issue", "Location", "Crew", ""]}
                    rows={filter === true ? filteredIssues : issues}
                    generateBody={(row: BayHandoverIssue, index: number) => generateTableBody(row, index)}
                    pagination={true}
                    defaultRowsPerPage={10}
                    skipIndexCol={true}
                />
            </div>
        </div>
    )
}

export default BayHandover;